import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addLocalStorageItem } from '../../util';
import { useDispatch } from 'react-redux';
import { setUserLogin, setUserRoles } from '../../store/reducers/account_reducer.js';
import { currentRole } from '../../store/reducers/menu_reducer.js';
import axios from '../../services/axios-config.js';
import { errorToast } from '../../util/toast.util.js';
import { upsertLocalStorageDetails } from '../../helpers/auth.helper.js';

const Login = () => {
  const [phone, setPhone] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [otp, setOtp] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otpToken, setOtpToken] = useState('');
  const [resendTimer, setResendTimer] = useState(0); // Timer state
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    let timerInterval;
    if (resendTimer > 0) {
      timerInterval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timerInterval);
  }, [resendTimer]);

  const handlePhoneChange = (e) => {
    setPhone(e.target.value);
    setErrorMessage('');
  };

  const handleOtpChange = (e) => {
    setOtp(e.target.value);
    setErrorMessage('');
  };

  const handleSendOtp = () => {
    setErrorMessage('');
    axios
      .post(process.env.REACT_APP_BASE_URL + '/v1/auth/send-otp', { phone })
      .then((response) => {
        if (response.status === 200) {
          setIsOtpSent(true);
          setOtpToken(response.data.data.otpToken);
          setOtpMessage(response.data.data.webInfoMessage);
          setResendTimer(30);
        }
      })
      .catch((error) => setErrorMessage(error.response?.data?.data?.message || 'Error sending OTP'));
  };

  const whoAmI = async () => {
    try {
      const response = await axios.get('/v1/users/me');
      const userRoles = response.data.data.tokens.map((roleToken) => roleToken.role);
      // Setting other information to roles
      upsertLocalStorageDetails(
        {
          roles: userRoles,
        },
        'master'
      );
      const roleTokens = response.data.data.tokens;
      dispatch(setUserRoles(userRoles));

      // checking for priotiry roles
      let currentRoleObject = roleTokens.find((roleToken) => roleToken.role === 'admin' || roleToken.role === 'super-admin');
      if (!currentRoleObject) {
        currentRoleObject = roleTokens[0];
      }
      // Saving current role to local storage
      upsertLocalStorageDetails(
        {
          displayName: response.data.data.name,
          role: currentRoleObject.role,
          token: currentRoleObject.token,
          locationId: currentRoleObject.locationId,
          companyId: currentRoleObject.companyId,
        },
        'role'
      );
      // If role is vehicle manage, then setting up 0 index by default
      if (currentRoleObject.locationId && currentRoleObject.role === 'vehicle-manager') {
        addLocalStorageItem('factoryId', currentRoleObject.locationId?.[0]);
      }
      if (currentRoleObject.locationId && currentRoleObject.role === 'machine-manager') {
        addLocalStorageItem('factoryId', currentRoleObject.locationId?.[0]);
      }
      dispatch(currentRole(currentRoleObject.role));
      dispatch(setUserLogin());
      navigate('/dashboard');
    } catch (error) {
      errorToast(error.response?.data?.data?.message || 'Session expired');
    }
  };

  const handleVerifyOtp = async () => {
    try {
      setOtpMessage('');
      const verifyOtpResponse = await axios.post(process.env.REACT_APP_BASE_URL + '/v1/auth/verify-otp', { phone, otpToken, otp });
      const data = verifyOtpResponse.data.data;
      // Saving master token to local storage
      upsertLocalStorageDetails(
        {
          masterToken: data.accessToken,
          displayName: data.name,
        },
        'master'
      );
      // Making me call to identify all roles and tokens
      await whoAmI();
    } catch (error) {
      setErrorMessage(error.response?.data?.data?.message || 'Error verifying OTP');
    }
  };

  useEffect(() => {
    whoAmI();
  }, []);

  return (
    <div style={{ overflow: 'hidden' }}>
      <div className="login-parent-wrapper row">
        <div className="login-wrapper">
          <div className="login-logo">
            <img src={`${process.env.PUBLIC_URL}/logo70.png`} alt="logo" />
          </div>
          {errorMessage && (
            <div className="alert alert-danger mt-3" role="alert">
              {errorMessage}
            </div>
          )}
          {otpMessage && (
            <div className="alert alert-success mt-3" role="alert">
              {otpMessage.split('<br>').map((line, index) => (
                <span key={line}>
                  {line}
                  <br />
                </span>
              ))}
            </div>
          )}
          <div className="logo-field-wrapper">
            <div className="row">
              <div className="form-group col-12">
                <label className="form-label" htmlFor="phone">
                  Phone Number
                </label>
                <input
                  type="text"
                  id="phone"
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="Enter phone number"
                  className="form-control"
                  required
                  disabled={isOtpSent}
                />
              </div>
              {isOtpSent && (
                <>
                  <div className="form-group col-12">
                    <label className="form-label" htmlFor="otp">
                      OTP
                    </label>
                    <input
                      type="text"
                      id="otp"
                      value={otp}
                      onChange={handleOtpChange}
                      placeholder="Enter OTP"
                      className="form-control"
                      required
                    />
                  </div>
                  <button className="btn app-btn-primary mt-2" onClick={handleVerifyOtp}>
                    Verify OTP
                  </button>
                  {resendTimer > 0 ? (
                    <span className="text-end text-white mt-2">Resend OTP in {resendTimer}s</span>
                  ) : (
                    <span
                      style={{ cursor: 'pointer' }}
                      className="text-end text-white mt-2 text-decoration-underline"
                      onClick={handleSendOtp}
                      disabled={resendTimer > 0}
                    >
                      Resend OTP
                    </span>
                  )}
                </>
              )}
              {!isOtpSent && (
                <button className="btn app-btn-primary mt-2" onClick={handleSendOtp}>
                  Send OTP
                </button>
              )}
            </div>
          </div>
        </div>
        {process.env.REACT_APP_NODE_ENV === 'production' && (
          <div className="text-center mt-4">
            <h6 className="footer_link text-white">
              Developed by{' '}
              <a href="https://vraio.in/" target="_blank" rel="noopener noreferrer">
                Vraio Software Solutions Pvt Ltd
              </a>
            </h6>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
